import { useTheme } from '@shopify/restyle';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { Switch } from 'app/design/primitives/switch';

import { AnimatedSignal } from 'app/design/icons/signal';
import React, { useState } from 'react';
import { type LayoutChangeEvent, Pressable, View } from 'react-native';
import { ListingMatchStatus, type NeedMatch, ListingType, Request, UpdateListingDTO } from 'app/types/listing';
import { PCImage } from 'app/design/primitives/image';
import { Trans, useTranslation } from 'react-i18next';
import { type TFunction } from 'i18next';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { useNavigation } from 'app/hooks/navigation';
import type { Theme } from 'app/shopify';
import { IconHelpCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

const RESPONDED_STATUSES: ListingMatchStatus[] = [ListingMatchStatus.ACCEPTED, ListingMatchStatus.MATCH];
const CONTACTED_STATUSES: ListingMatchStatus[] = [
  ListingMatchStatus.SENT,
  ListingMatchStatus.EXPIRED,
  ListingMatchStatus.INITIATED,
  ListingMatchStatus.RESOLVED, // The backend will filter out RESOLVED matches where the candidateUser never responded
];

const AVATAR_SIZE = 36;

const VISIBLE_STATUSES: ListingMatchStatus[] = [...RESPONDED_STATUSES, ...CONTACTED_STATUSES];

function CandidateAvatar({
  nm,
  navigation,
  hasLender,
}: {
  nm: NeedMatch;
  navigation: ReturnType<typeof useNavigation>;
  hasLender?: boolean;
}) {
  const theme = useTheme<Theme>();
  const isTheMatch = nm.status === ListingMatchStatus.MATCH;
  const hasResponded = RESPONDED_STATUSES.includes(nm.status);
  const isRejected = nm.status === ListingMatchStatus.REJECTED;
  return (
    <Pressable
      onPress={() => {
        if (nm.candidateUser?.id) navigateToUserProfile(navigation, nm.candidateUser.id);
      }}
      style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
    >
      <View
        style={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          borderRadius: AVATAR_SIZE / 2,
          overflow: 'hidden',
          opacity: isTheMatch ? 1 : hasLender ? 0.55 : hasResponded ? 1 : 0.55,
          ...(isTheMatch && hasLender ? { borderWidth: 2, borderColor: theme.colors['text.positive'] } : undefined),
        }}
      >
        <PCImage
          source={{ uri: nm.candidateUser?.profile.picture ?? undefined }}
          style={{ width: '100%', height: '100%' }}
        />
      </View>
      {hasResponded ? (
        <View
          style={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            width: 16,
            height: 16,
            borderRadius: 8,
            backgroundColor: isRejected ? theme.colors['surface.error.default'] : theme.colors['text.positive'],
            borderWidth: 2,
            borderColor: theme.colors['surface.primary-neutral'],
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text style={{ color: '#fff', fontSize: 9, fontWeight: '700', lineHeight: 12 }}>
            {isRejected ? '✕' : '✓'}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

function CandidateAvatars({
  needMatches,
  theme,
  t,
  navigation,
  hasLender,
  onPressViewResponses,
  numActiveMatches,
}: {
  needMatches?: NeedMatch[];
  theme: Theme;
  t: TFunction<'one-listing'>;
  navigation: ReturnType<typeof useNavigation>;
  hasLender?: boolean;
  onPressViewResponses?: () => void;
  numActiveMatches: number;
}) {
  const [hasWrapped, setHasWrapped] = useState(false);
  const onRowLayout = (e: LayoutChangeEvent) => {
    // A single row of avatars is 36px tall; if the row is taller, the button wrapped
    setHasWrapped(e.nativeEvent.layout.height > AVATAR_SIZE * 1.5);
  };

  const visible = needMatches?.filter((nm) => VISIBLE_STATUSES.includes(nm.status));
  // Show responded first, then contacted
  const sorted = [
    ...(visible?.filter((nm) => RESPONDED_STATUSES.includes(nm.status)) ?? []),
    ...(visible?.filter((nm) => CONTACTED_STATUSES.includes(nm.status)) ?? []),
  ];
  const maxVisible = 6;
  const overflow = sorted.length - maxVisible;

  if (sorted.length === 0) return null;

  return (
    <Column gap="spacing.general.x1">
      <Text style={{ fontSize: 14, fontWeight: '700', lineHeight: 24, color: theme.colors['text.primary'] }}>
        {t('automaticMessageCard.contacted')}
      </Text>
      <Row
        onLayout={onRowLayout}
        style={{
          alignContent: 'center',
          flexWrap: 'wrap',
          rowGap: theme.spacing['spacing.general.x2'],
          columnGap: theme.spacing['spacing.general.x2'],
        }}
      >
        {sorted.slice(0, maxVisible).map((nm) => (
          <CandidateAvatar
            key={nm.candidateUser?.id ?? `${nm.status}:${nm.candidateUser?.profile.picture ?? 'anonymous'}`}
            nm={nm}
            navigation={navigation}
            hasLender={hasLender}
          />
        ))}
        {overflow > 0 && (
          <View
            style={{
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              borderRadius: AVATAR_SIZE / 2,
              backgroundColor: theme.colors['text.placeholder'],
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text color="surface.default" fontSize={12}>
              +{overflow}
            </Text>
          </View>
        )}
        {onPressViewResponses ? (
          <Pressable
            onPress={onPressViewResponses}
            style={{
              borderRadius: theme.borderRadii['radius.xl'],
              borderWidth: 1,
              borderColor: theme.colors['text.primary'],
              paddingVertical: 10,
              paddingHorizontal: theme.spacing['spacing.general.x4'],
              marginBottom: hasLender ? theme.spacing['spacing.general.x2'] : 0,
              marginRight: hasWrapped ? 'auto' : undefined,
            }}
          >
            <Text variant="bodyBase" fontWeight="700">
              {t('automaticMessageCard.viewResponses', { count: numActiveMatches })}
            </Text>
          </Pressable>
        ) : null}
      </Row>
    </Column>
  );
}

interface ConciergerieSectionProps {
  listing: {
    type: ListingType;
    contactedMembers?: number | null;
    numActiveMatches?: number | null;
    numMatches?: number | null;
    conciergerieOff?: boolean | null;
    conciergerieDisabledByUser?: boolean | null;
    needMatches?: NeedMatch[];
    lender?: Request['lender'];
  };
  onUpdate: (data: Pick<UpdateListingDTO, 'conciergerieDisabledByUser'>) => void;
  onPressMoreDetails: () => void;
  onPressViewResponses?: () => void;
}

function getConciergerieState(listing: ConciergerieSectionProps['listing']) {
  const needMatches = listing.needMatches;
  const hasLender = Boolean(needMatches?.some((match) => match.status === ListingMatchStatus.MATCH) || listing.lender);
  const numContacted = needMatches?.filter((match) => match.status !== ListingMatchStatus.PENDING).length ?? 0;
  const numCandidates = needMatches?.length ?? 0;
  const noMatches = numCandidates === 0;
  const hasContactedAll = numContacted >= numCandidates;
  const isSearching = !listing.conciergerieOff && noMatches;
  const noCandidates = Boolean(listing.conciergerieOff && noMatches);
  const numActiveMatches =
    needMatches?.filter((match) =>
      (
        [ListingMatchStatus.ACCEPTED, ListingMatchStatus.MATCH, ListingMatchStatus.INITIATED] as ListingMatchStatus[]
      ).includes(match.status),
    ).length ?? 0;
  const hasResponses = numActiveMatches > 0;

  let extension = '';
  if (hasLender) extension = 'HasLender';
  else if (hasResponses) extension = 'WithResponses';
  else if (noCandidates) extension = 'NoCandidates';
  else if (isSearching) extension = 'Searching';
  else if (hasContactedAll) extension = 'ContactedAll';

  return {
    enableAnimation: !hasLender && (isSearching || !hasContactedAll),
    extension,
    hasLender,
    hasResponses,
    needMatches,
    numActiveMatches,
    numCandidates,
  };
}

function ConciergerieSignal({
  enableAnimation,
  hasResponses,
  numActiveMatches,
  theme,
}: {
  enableAnimation: boolean;
  hasResponses: boolean;
  numActiveMatches: number;
  theme: Theme;
}) {
  const backgroundColor = enableAnimation ? theme.colors['surface.tertiary'] : theme.colors['surface.disabled'];

  return (
    <View
      style={{
        width: 32,
        height: 32,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor,
      }}
    >
      <AnimatedSignal height={18} width={18} enableAnimation={enableAnimation} />
      {hasResponses ? (
        <Text
          fontSize={10}
          style={{
            position: 'absolute',
            top: 0,
            right: -theme.spacing['spacing.general.x1'],
            color: theme.colors['surface.default'],
            backgroundColor: theme.colors['surface.error.default'],
            borderRadius: theme.borderRadii['radius.full'],
            paddingHorizontal: theme.spacing['spacing.general.x1'],
          }}
        >
          {numActiveMatches}
        </Text>
      ) : null}
    </View>
  );
}

export function ConciergerieSection({
  listing,
  onUpdate,
  onPressMoreDetails,
  onPressViewResponses,
}: ConciergerieSectionProps) {
  const navigation = useNavigation();
  const theme = useTheme<Theme>();
  const { t } = useTranslation('one-listing');
  const { enableAnimation, extension, hasLender, hasResponses, needMatches, numActiveMatches, numCandidates } =
    getConciergerieState(listing);
  const [conciergerieEnabledOverride, setConciergerieEnabledOverride] = useState<boolean>();
  const conciergerieEnabled = conciergerieEnabledOverride ?? !listing.conciergerieDisabledByUser;
  const onChangeConciergerieEnabled = (enabled: boolean) => {
    setConciergerieEnabledOverride(enabled);
    onUpdate({ conciergerieDisabledByUser: !enabled });
  };

  if (hasLender) {
    return null;
  }

  return (
    <Column
      gap="spacing.general.x2"
      style={{
        borderRadius: theme.borderRadii['radius.lg'],
        backgroundColor: theme.colors['surface.primary-neutral'],
        padding: theme.spacing['spacing.general.x4'],
        paddingTop: theme.spacing['spacing.general.0'],
        marginTop: theme.spacing['spacing.general.x4'],
      }}
    >
      <Pressable
        style={{
          position: 'absolute',
          top: -5,
          right: -3,
          padding: theme.spacing['spacing.general.x2'],
          zIndex: 1,
        }}
        onPress={onPressMoreDetails}
      >
        <IconHelpCircle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.placeholder']} />
      </Pressable>
      <Row alignItems="center" gap="spacing.general.x2" style={{ width: '95%' }}>
        <ConciergerieSignal
          enableAnimation={enableAnimation}
          hasResponses={hasResponses}
          numActiveMatches={numActiveMatches}
          theme={theme}
        />
        <Column gap="spacing.general.x2" style={{ flex: 1 }}>
          <Text variant="bodyBase" fontSize={15} fontWeight="700" width="85%">
            {t(`automaticMessageCard.title${extension}`)}
          </Text>
        </Column>
      </Row>
      <Row alignItems="center" justifyContent="space-between">
        <Text variant="bodyBase">{t(`automaticMessageCard.${conciergerieEnabled ? 'enabled' : 'disabled'}`)}</Text>
        <Switch value={conciergerieEnabled} onValueChange={onChangeConciergerieEnabled} />
      </Row>
      <Text>
        <Trans
          ns="one-listing"
          i18nKey={`automaticMessageCard.subtitle${extension}`}
          components={{ strong: <Text fontWeight="700" /> }}
        />
      </Text>

      {numCandidates > 0 ? (
        <CandidateAvatars
          numActiveMatches={numActiveMatches}
          onPressViewResponses={onPressViewResponses}
          hasLender={hasLender}
          navigation={navigation}
          needMatches={needMatches}
          theme={theme}
          t={t}
        />
      ) : null}
    </Column>
  );
}
